import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Users, CalendarPlus, CalendarClock, Mars, Venus, Repeat, IndianRupee, MousePointerClick } from "lucide-react";

const TOPS = [100, 500, 1000];

const Tile = ({ icon: Icon, title, sub, active, onClick, testid, children }) => (
  <Card onClick={onClick} className={`p-4 cursor-pointer border transition-colors ${active ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/50"}`} data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center text-primary shrink-0"><Icon size={20} /></div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm leading-tight">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
        {children}
      </div>
    </div>
  </Card>
);

// Simple "Select Customers" for daily use. Emits a quick-filter object consumed by the existing audience engine.
export const QuickSelect = ({ value, onPick }) => {
  const [counts, setCounts] = useState(null);
  const [n, setN] = useState({ top_visits: "", top_paid: "" });
  useEffect(() => { api.get("/campaigns/quick-counts").then((r) => setCounts(r.data)).catch(() => setCounts({})); }, []);
  const c = (k) => (counts ? (counts[k] ?? "—").toLocaleString?.() ?? counts[k] : "…");
  const is = (q) => value?.quick === q;
  const topTile = (q, icon, title, sub) => (
    <Tile icon={icon} title={title} sub={sub} active={is(q)} testid={`qs-${q}`} onClick={() => !is(q) && onPick({ quick: q, top_n: 100 })}>
      <div className="flex flex-wrap items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
        {TOPS.map((t) => <Button key={t} size="sm" variant={is(q) && value.top_n === t ? "default" : "outline"} className="h-7 rounded-sm text-xs" onClick={() => onPick({ quick: q, top_n: t })} data-testid={`qs-${q}-${t}`}>Top {t.toLocaleString()}</Button>)}
        <Input type="number" min="1" placeholder="Custom" value={n[q]} onChange={(e) => setN({ ...n, [q]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && Number(n[q]) > 0 && onPick({ quick: q, top_n: Number(n[q]) })} className="h-7 w-20 bg-secondary text-xs" data-testid={`qs-${q}-custom`} />
        <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" disabled={!(Number(n[q]) > 0)} onClick={() => onPick({ quick: q, top_n: Number(n[q]) })} data-testid={`qs-${q}-custom-apply`}>Go</Button>
      </div>
    </Tile>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-select">
      <Tile icon={Users} title="All Customers" sub={`${c("all")} unique customers till now`} active={is("all")} testid="qs-all" onClick={() => onPick({ quick: "all" })} />
      <Tile icon={CalendarPlus} title="New Customers – This Month" sub={`${c("new_this_month")} · first paid visit this month`} active={is("new_this_month")} testid="qs-new_this_month" onClick={() => onPick({ quick: "new_this_month" })} />
      <Tile icon={CalendarClock} title="New Customers – Last Month" sub={`${c("new_last_month")} · first paid visit last month`} active={is("new_last_month")} testid="qs-new_last_month" onClick={() => onPick({ quick: "new_last_month" })} />
      <Tile icon={Mars} title="Male Customers" sub={`${c("male")} customers`} active={is("male")} testid="qs-male" onClick={() => onPick({ quick: "male" })} />
      <Tile icon={Venus} title="Female Customers" sub={`${c("female")} customers`} active={is("female")} testid="qs-female" onClick={() => onPick({ quick: "female" })} />
      {topTile("top_visits", Repeat, "Highest Visiting Customers", "ranked by completed / paid visits")}
      {topTile("top_paid", IndianRupee, "Highest Paid Customers", "ranked by lifetime bill total")}
      <Tile icon={MousePointerClick} title="Select Custom Customers" sub="search by name / mobile and tick manually" active={is("custom")} testid="qs-custom" onClick={() => onPick({ quick: "custom" })} />
    </div>
  );
};

export const CampaignImage = ({ template, mediaUrl, onChange, backend }) => {
  const [busy, setBusy] = useState(false);
  if (!template) return null;
  if ((template.header_type || "none") !== "image") {
    return <p className="text-xs text-muted-foreground" data-testid="camp-image-unsupported">This template does not support an image.</p>;
  }
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/campaigns/upload-media", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.media_url); }
    catch (e) { alert(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const src = mediaUrl ? `${backend}${mediaUrl}` : template.media_url ? `${backend}${template.media_url}` : "";
  return (
    <div className="flex items-center gap-3" data-testid="camp-image">
      {src ? <img src={src} alt="campaign" className="h-20 w-32 object-cover rounded-sm border border-border" data-testid="camp-image-preview" /> : <div className="h-20 w-32 rounded-sm border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">No image</div>}
      <div className="text-xs space-y-1">
        <p className="font-medium">Campaign image (IMAGE header)</p>
        <p className="text-muted-foreground">{mediaUrl ? "Stored with this campaign — Send Now, Scheduled and Test Send all use it." : "Upload the image customers will see as the template header (JPG/PNG, under 5 MB)."}</p>
        <label className="inline-flex items-center gap-2 cursor-pointer text-primary">
          <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="camp-image-input" />
          <span className="underline">{busy ? "Uploading…" : mediaUrl ? "Replace image" : "Upload image"}</span>
        </label>
      </div>
    </div>
  );
};
